import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../supabaseClient';

/**
 * Create List Modal Component
 * Allows users to create formatted lists in chat channels
 */
const CreateListModal = ({ channelId, onClose, onListCreated }) => {
  const [title, setTitle] = useState('');
  const [items, setItems] = useState([{ text: '', subtitle: '' }]);
  const [isCreating, setIsCreating] = useState(false);

  /**
   * Add a new list item
   */
  const handleAddItem = () => {
    if (items.length < 20) {
      setItems([...items, { text: '', subtitle: '' }]);
    }
  };

  /**
   * Remove a list item
   */
  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  /**
   * Update item text or subtitle
   */
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  /**
   * Create the list
   */
  const handleCreateList = async () => {
    // Validation
    const validItems = items.filter(item => item.text.trim());
    if (validItems.length === 0) {
      alert('Please add at least one item');
      return;
    }

    setIsCreating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Store list items with checked state as JSON in message_text
      const listItems = validItems.map(item => ({
        text: item.text.trim(),
        subtitle: item.subtitle.trim(),
        checked: false
      }));

      // Create list record with JSON data in message_text
      const listPayload = {
        __type: 'list',
        list_title: title.trim(),
        list_items: listItems
      };

      const { data: listData, error: listError } = await supabase
        .from('team_chat')
        .insert({
          record_type: 'message',
          channel_id: channelId,
          user_id: user.id,
          message_text: JSON.stringify(listPayload),
          created_by: user.id
        })
        .select()
        .single();

      if (listError) throw listError;

      onListCreated(listData);
      onClose();
    } catch (error) {
      console.error('Error creating list:', error);
      alert('Failed to create list: ' + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-lg w-full rounded-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold text-white">Create List</h2>
            <p className="text-sm text-gray-400">Add items to your list</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="List title"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
              maxLength={100}
            />
          </div>

          {/* List Items */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Items
            </label>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center w-8 text-gray-400 text-sm font-medium">
                      {index + 1}.
                    </div>
                    <input
                      type="text"
                      value={item.text}
                      onChange={(e) => handleItemChange(index, 'text', e.target.value)}
                      placeholder={`Item ${index + 1}`}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      maxLength={200}
                    />
                    {items.length > 1 && (
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="w-5 h-5 text-red-400" />
                      </button>
                    )}
                  </div>
                  {/* Subtitle input */}
                  <div className="flex gap-2">
                    <div className="w-8" />
                    <input
                      type="text"
                      value={item.subtitle}
                      onChange={(e) => handleItemChange(index, 'subtitle', e.target.value)}
                      placeholder="Subtitle (optional)"
                      className="flex-1 px-4 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-300 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                      maxLength={150}
                    />
                  </div>
                </div>
              ))}
            </div>
            {items.length < 20 && (
              <button
                onClick={handleAddItem}
                className="mt-2 flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-50 rounded-lg text-white font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateList}
            disabled={isCreating}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
          >
            {isCreating ? 'Creating...' : 'Send List'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateListModal;
